// @ts-ignore
import {vuexOidcCreateStoreModule, vuexOidcCreateUserManager} from "vuex-oidc"
import {ActionContext, Module, Store} from "vuex"
import {AppState} from "./index"
import {User, UserManager} from "oidc-client"
import logger from "@/utils/logging"
import {Route} from "vue-router"
import apiService from "@/services/api.service"
import {appConfig} from "@/config/app.config"
import Tracker from "@/services/tracking.service"

export interface AuthState {
    access_token: string,
    error: string,
    events_are_bound: boolean,
    id_token: string,
    user: {
        sub: string,
        preferred_username: string,
    }
}

export const namespace = "auth"

const tempModule: Module<AuthState, AppState> = vuexOidcCreateStoreModule(appConfig.OIDC_SETTINGS, {namespaced: true},
    // Optional OIDC event listeners
    {
        userLoaded: (user: User) => {
            if (user.profile.preferred_username) {
                Tracker.setUserId(user.profile.preferred_username)
            }
            return apiService.setAuthenticationToken(user.access_token)
        },
    })
export const module: Module<AuthState, AppState> = {
    ...tempModule,
    actions: {
        ...tempModule.actions,
        assureInit: async (context: ActionContext<AuthState, AppState>) => {
            // If no user is available already, we try to load it from the local storage, so we don't perform the authentication
            // redirect at every navigation
            if (context.state.user === null) {
                logger.debug("[Auth] Loading user from local storage...")
                const manager: UserManager = vuexOidcCreateUserManager(appConfig.OIDC_SETTINGS)
                const user = await manager.getUser()
                logger.debug("[Auth] Loaded locally stored user:", user)
                if (user !== null) {
                    if (!user.expired) {
                        // Save the new user
                        context.commit("setOidcAuth", user)
                        if (user.profile.preferred_username) {
                            Tracker.setUserId(user.profile.preferred_username)
                        }
                        apiService.setAuthenticationToken(user.access_token)
                    }
                    return true
                }
                return false
            } else {
                return true
            }
        },
        signOut: async (context: ActionContext<AuthState, AppState>) => {
            localStorage.clear()
            Tracker.clearUser()
            // @ts-ignore
            return tempModule.actions.signOutOidc()
        },
    },
}

/** Since VueX doesn't currently support fully typing things, we can define the signatures by hand. */
export function wrapGetters(store: Store<AppState>) {
    return {
        isAuthenticated(): boolean {
            return store.getters[`${namespace}/oidcIsAuthenticated`]
        },
    }
}

/** Since VueX doesn't currently support fully typing things, we can define the signatures by hand. */
export function wrapActions(store: Store<AppState>) {
    return {
        checkHasAuthenticated(): Promise<boolean> {
            return store.dispatch(`${namespace}/assureInit`)
        },
        checkAccess(route: Route): Promise<boolean> {
            return store.dispatch(`${namespace}/oidcCheckAccess`, route)
        },
        oidcSignInCallback(): Promise<string> {
            return store.dispatch(`${namespace}/oidcSignInCallback`)
        },
        signOut(): Promise<void> {
            return store.dispatch(`${namespace}/signOut`)
        },
    }
}
